import { useState } from 'react';
import { useEditor } from './EditorContext';

/**
 * Full-editor overlay shown when a change failed to persist. It captures all
 * pointer interaction (so contentEditable fields and drag-and-drop are blocked,
 * not just disabled buttons) and offers a Reload that re-syncs with the server
 * and clears the block.
 */
export function SaveBlockedOverlay() {
  const { blockReason, reloadFromServer } = useEditor();
  const [reloading, setReloading] = useState(false);

  if (!blockReason) return null;

  const isAuthError = blockReason.status === 401 || blockReason.status === 403;

  const onReload = async () => {
    setReloading(true);
    try {
      await reloadFromServer();
    } finally {
      setReloading(false);
    }
  };

  return (
    <div
      className="save-blocked-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(15, 18, 25, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.5rem',
      }}
      // Capture every interaction so nothing underneath can be edited.
      onPointerDownCapture={(e) => e.stopPropagation()}
      onKeyDownCapture={(e) => e.stopPropagation()}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="save-blocked-title"
        aria-describedby="save-blocked-desc"
        className="save-blocked-dialog"
        style={{
          background: 'var(--surface, #fff)',
          color: 'var(--text, #1a1a1a)',
          borderRadius: 12,
          maxWidth: 460,
          width: '100%',
          padding: '1.5rem',
          boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
        }}
      >
        <h2 id="save-blocked-title" style={{ margin: '0 0 0.5rem', fontSize: '1.15rem' }}>
          ⚠ Your changes are not being saved
        </h2>
        <p id="save-blocked-desc" style={{ margin: '0 0 0.5rem', lineHeight: 1.45 }}>
          {blockReason.message}
        </p>
        <p style={{ margin: '0 0 1.25rem', opacity: 0.75, fontSize: '0.9rem' }}>
          {isAuthError
            ? 'Sign in again to resume editing. Your unsaved changes will be lost.'
            : "Editing is paused so you don't lose work against a stale copy. Reload to fetch the latest saved version from the server and continue."}
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          {isAuthError ? (
            <button className="btn btn-primary" onClick={() => window.location.reload()}>
              Sign in again
            </button>
          ) : (
            <button className="btn btn-primary" onClick={() => void onReload()} disabled={reloading}>
              {reloading ? 'Reloading…' : 'Reload'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
